import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Lock, ArrowLeft, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

export default function ResetPasswordPage() {
  usePageTitle("Reinitialiser le mot de passe");
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!password || password.length < 6) { setError("Le mot de passe doit contenir au moins 6 caracteres."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    if (!token) { setError("Lien invalide. Veuillez refaire une demande."); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Erreur lors de la reinitialisation."); return; }
      setSuccess(true);
    } catch {
      setError("Erreur serveur. Veuillez reessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#080C18] flex items-center justify-center px-4" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <div className="bg-[#0F1629] border border-slate-700/50 rounded-2xl p-8">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Lien invalide</h2>
            <p className="text-slate-400 text-sm mb-6">Ce lien de reinitialisation est invalide ou incomplet.</p>
            <Link to="/mot-de-passe-oublie" className="text-[#0099CC] hover:text-[#00BBEE] text-sm font-medium">
              Refaire une demande
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C18] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-glotech-icon.png" alt="GLOTECH" className="h-14 w-14 mx-auto rounded-xl object-cover" />
        </div>

        <div className="bg-[#0F1629] border border-slate-700/50 rounded-2xl p-8">
          {success ? (
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Mot de passe modifie</h2>
              <p className="text-slate-400 text-sm">
                Votre mot de passe a ete reinitialise avec succes. Vous pouvez maintenant vous connecter.
              </p>
              <Link
                to="/login"
                data-testid="reset-go-to-login"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0099CC] to-[#0077AA] text-white px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity mt-2"
              >
                Se connecter
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-white mb-1">Nouveau mot de passe</h2>
              <p className="text-slate-400 text-sm mb-6">Choisissez un nouveau mot de passe securise.</p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3 mb-4" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1.5">Nouveau mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      data-testid="reset-password-input"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg pl-10 pr-12 py-3 text-white focus:border-[#0099CC] outline-none transition-colors"
                      placeholder="Minimum 6 caracteres"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1.5">Confirmer le mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      data-testid="reset-password-confirm"
                      type={showPw ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#0099CC] outline-none transition-colors"
                      placeholder="Retapez le mot de passe"
                    />
                  </div>
                  {password && confirm && password !== confirm && (
                    <p className="text-red-400 text-xs mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                </div>
                <button
                  data-testid="reset-password-submit"
                  type="submit"
                  disabled={loading || !password || !confirm}
                  className="w-full bg-gradient-to-r from-[#0099CC] to-[#0077AA] text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reinitialiser le mot de passe"}
                </button>
              </form>

              <div className="text-center mt-6">
                <Link to="/login" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm">
                  <ArrowLeft className="w-4 h-4" /> Retour a la connexion
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
